import { FactorLensDataType, FactorLensFocus } from "@/interfaces/factorLens.interface"
import { FactorContrib, FactorPortContrib } from "@/interfaces/raDashboard.interface"
import React, { useMemo } from "react"
import { FactorLensContext, FactorLensTopBottom } from "."

export const useFactorLensContext = () => {
  const ctx = React.useContext(FactorLensContext);
  return ctx;
}

export const useViewedFactor = () => {
  const { allFactorData, selectedDataType } = useFactorLensContext();
  return allFactorData[selectedDataType];
}

export const useViewedFactorType = () => {
  const { selectedDataType } = useFactorLensContext();
  return selectedDataType;
}

export const useFactorFocus = () => {
  const { selectedFactorBox } = useFactorLensContext();
  return selectedFactorBox;
}

export const useSelectedFactors = () => {
  const { allFactorData, selectedDataType, selectedFactorBox } = useFactorLensContext();
  const selected = useMemo(() => {
    return getSelectedFactors(allFactorData[selectedDataType].factors, selectedFactorBox);
  }, [allFactorData, selectedDataType, selectedFactorBox])
  return selected;
}

export const usePortfolioSelectedFactors = () => {
  const { portfolioSecurities, selectedDataType, selectedFactorBox } = useFactorLensContext();
  const selected = useMemo(() => {
    return getSelectedFactors(portfolioSecurities, selectedFactorBox) as FactorPortContrib[];
  }, [portfolioSecurities, selectedDataType, selectedFactorBox])
  return selected;
}

const EMPTY_TOP_BOTTOM: FactorLensTopBottom = { topItems: [], bottomItems: [] };
export const useTopBottomSelectedPortfolioFactors = () => {
  const viewedFactors = useViewedFactorType();
  const factorFocus = useFactorFocus();
  const selectedFactors = usePortfolioSelectedFactors(); 
  const topBottom = useMemo((): FactorLensTopBottom => {
    if (viewedFactors !== FactorLensDataType.Portfolio || factorFocus?.name === 'Specific Risk') {
      return EMPTY_TOP_BOTTOM;
    }
    const endContribCount = 5;
    const filteredFactors = selectedFactors.filter(f => f.level1 !== 'Specific Risk');
    const sorted = filteredFactors.sort((factorA, factorB) => (factorB.risk_contrib ?? 0) - (factorA.risk_contrib ?? 0));
    if (filteredFactors.length <= endContribCount) {
      return {
        topItems: sorted,
        bottomItems: sorted.reverse()
      };
    }
    return {
      topItems: sorted.slice(0, endContribCount),
      bottomItems: sorted.slice(-endContribCount).reverse()
    }
  }, [selectedFactors, viewedFactors, factorFocus]);
  return topBottom;
}

export const useSelectedFactorBox = () => {
  const ctx = useFactorLensContext();
  return ctx.selectedFactorBox;
}

function getSelectedFactors(factors: FactorContrib[], focus: FactorLensFocus | null) {
  if (factors === null) return [];

  // Collapse all Specific Risk records into one synthetic row — the API returns
  const specificRiskRecords = factors.filter(f => f.level1 === 'Specific Risk');
  const nonSpecificRiskRecords = factors.filter(f => f.level1 !== 'Specific Risk');
  const specificRiskRow: FactorContrib[] = specificRiskRecords.length > 0
    ? [{
        ...specificRiskRecords[0],
        level2: 'Specific Risk',
        level3: 'Specific Risk',
        level4: 'Specific Risk',
        level5: 'Specific Risk',
        factor_name: 'Specific Risk',
        risk_contrib: specificRiskRecords.reduce((sum, f) => sum + (f.risk_contrib ?? 0), 0),
        weighted_exposure: null,
        factor_risk: null,
      }]
    : [];

  if (focus === null || focus.level === 0 || focus.name === 'Total Risk') {
    return [...nonSpecificRiskRecords, ...specificRiskRow];
  }

  if (focus.level > 5 || focus.level < 1) return [];
  //Filter at the specified level
  if (focus.level == 1) {
    if (focus.name === 'Specific Risk') return specificRiskRow;
    return nonSpecificRiskRecords.filter(factors => factors.level1 == focus.name);
  }
  if (focus.level == 2) return factors.filter(factors => factors.level2 == focus.name);
  if (focus.level == 3) return factors.filter(factors => factors.level3 == focus.name);
  if (focus.level == 4) return factors.filter(factors => factors.level4 == focus.name);
  if (focus.level == 5) return factors.filter(factors => factors.level5 == focus.name);
  return [];
}